import React, { forwardRef } from 'react'
import { PureQueryOptions } from 'apollo-client'
import { IconTag } from '@contrast-security-inc/design-system-foundations'
import { LibraryAgentLanguage } from '../library/library-agent-language'
import {
  ModifyLibrariesTagsByFilterModal,
  ModifyLibrariesTagsModal
} from './tag-modal'
import { useIntl } from 'react-intl'
import {
  ConfirmationAction,
  ConfirmationRoleAction
} from 'contrastBase/ConfirmationRoleAction'
import { getInitialTags } from 'contrastBase/Tags'
import {
  ConfirmationRoleActionProps,
  Role
} from '@contrast-security-inc/base-types'
import { Text } from '@contrast-security-inc/design-system-typography'
import { css } from '@emotion/css'

export const actionTestID = 'library-summary-tag-action-icon'
export const rowActionTestId = `libraries-tag-action-icon-`

const tagWithCountCss = css`
  display: flex;
  text-align: center;
  align-items: center;
  gap: 2px;
`

interface Library {
  hash: string
  language: LibraryAgentLanguage
  tags: string[]
}

interface BulkActionProps {
  libraries: Library[]
  organizationID: string
  showDisabled?: boolean
  applicationRole: Role
  refetchQueries?: PureQueryOptions[]
  refresh: () => Promise<any> | (() => void)
  bulk?: boolean
  isPanel?: boolean
}

export function ModifyLibrariesTagsAction({
  libraries,
  organizationID,
  showDisabled,
  applicationRole,
  refetchQueries,
  refresh,
  bulk = false,
  isPanel
}: BulkActionProps) {
  const { formatMessage } = useIntl()
  const tagCount =
    libraries.length === 0 || !libraries[0].tags ? 0 : libraries[0].tags.length
  const showTagCount =
    !isPanel && tagCount > 0 && libraries.length === 1 && !bulk

  return (
    <ConfirmationRoleAction
      role={applicationRole}
      isActionValid={true}
      showDisabled={showDisabled}
      requiredRole="VIEW"
      icon={forwardRef(({ className }, _ref) => (
        <div className={tagWithCountCss}>
          {showTagCount ? (
            <Text color="neutral80" font="metadata">
              ({tagCount < 99 ? tagCount : '99+'})
            </Text>
          ) : null}
          <IconTag
            aria-label={formatMessage({ id: 'action_tag' })}
            className={className}
            data-testid={
              isPanel || bulk
                ? actionTestID
                : `${rowActionTestId}-${libraries[0].hash}`
            }
            size="md"
          />
        </div>
      ))}
      iconTooltipMessage={formatMessage({ id: 'action_tag' })}
      iconTooltipPlacement="top"
      renderDialog={({ isOpen, closeDialog }) => {
        return (
          <ModifyLibrariesTagsModal
            organizationID={organizationID}
            libraries={libraries}
            closeModal={closeDialog}
            isOpen={isOpen}
            initialTags={getInitialTags(libraries)}
            refetchQueries={refetchQueries}
            refresh={refresh}
          />
        )
      }}
    />
  )
}

type BulkByFilterActionProps = Pick<ConfirmationRoleActionProps, 'size'> & {
  libraries: Library[]
  organizationID: string
  refetchQueries?: PureQueryOptions[]
  refresh: () => Promise<any> | (() => void)
  excludedHashes: string[]
  filters: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  additionalFilters?: { [key: string]: any }
}

export function ModifyLibrariesTagsByFilterAction({
  libraries,
  organizationID,
  excludedHashes,
  filters,
  additionalFilters,
  refetchQueries,
  refresh,
  size
}: BulkByFilterActionProps) {
  const { formatMessage } = useIntl()

  return (
    <ConfirmationAction
      icon={IconTag}
      iconTooltipMessage={formatMessage({ id: 'action_tag' })}
      size={size}
      iconTooltipPlacement="top"
      renderDialog={({ isOpen, closeDialog }) => {
        return (
          <ModifyLibrariesTagsByFilterModal
            organizationID={organizationID}
            excludedHashes={excludedHashes}
            isOpen={isOpen}
            closeModal={closeDialog}
            initialTags={getInitialTags(libraries)}
            filters={filters}
            additionalFilters={additionalFilters}
            refetchQueries={refetchQueries}
            refresh={refresh}
            libraries={libraries}
          />
        )
      }}
    />
  )
}
